import React, { FC, useEffect, useState } from 'react';
import { omit } from 'lodash';
import { useTranslate } from 'react-admin';
import { Field, useForm, useFormState } from 'react-final-form';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { H3 } from '~/components';
import AutocompleteArrayTextInputComponent from '~/components/AutocompleteArrayTextInput';
import { useAllRoles } from '~/hooks/roles';
import { isValidEmail } from '~/utils/validateEmail';
import { l } from '~/utils/solutions';

interface TenantNotificationsFormProps {
  keyName: string;
}

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  roleField: {
    width: 240,
    flexShrink: 0,
  },
  emailField: {
    flex: 1,
    minWidth: 0,
  },
  deleteButton: {
    marginTop: theme.spacing(1),
  },
}));

const getNestedValue = (obj: any, path: string): any =>
  path.split('.').reduce((acc: any, part) => acc?.[part], obj);

interface Pair {
  id: number;
  key: string;
  value: string[];
}

const RoleGroupEmailsEditor: FC<{ source: string }> = ({ source }) => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const localClasses = useStyles();
  const translate = useTranslate();
  const t = (key: string, options?: any): string => translate(l(`featuresConfig.${key}`), options);

  const { data: roles = [] } = useAllRoles();

  const emailMap: Record<string, string[]> = getNestedValue(values, source) ?? {};

  const [pairs, setPairs] = useState<Pair[]>(() =>
    Object.entries(emailMap).map(([k, v]) => ({ id: Math.random(), key: k, value: v }))
  );
  const [emailErrors, setEmailErrors] = useState<Record<number, string | undefined>>({});

  const syncToForm = (newPairs: Pair[]) => {
    const obj: Record<string, string[]> = {};
    newPairs.forEach(({ key, value }) => {
      const validEmails = value?.filter(isValidEmail) ?? [];
      if (key && validEmails.length > 0) obj[key] = validEmails;
    });
    form.change(source, Object.keys(obj).length > 0 ? obj : undefined);
  };

  const getEmailsError = (value: string[]): string | undefined => {
    const invalidEmails = value.filter((email) => !isValidEmail(email));
    if (invalidEmails.length > 0) {
      return t('tenantNotificationsInvalidEmail', {
        smart_count: invalidEmails.length,
        emails: invalidEmails.join(', '),
      });
    }
    return undefined;
  };

  const handleKeyChange = (id: number, newKey: string) => {
    const newPairs = pairs.map((p) => (p.id === id ? { ...p, key: newKey } : p));
    setPairs(newPairs);
    syncToForm(newPairs);
  };

  const handleValueChange = (id: number, newValue: string[]) => {
    const newPairs = pairs.map((p) => (p.id === id ? { ...p, value: newValue } : p));
    setPairs(newPairs);
    syncToForm(newPairs);
    setEmailErrors((prev) => ({
      ...prev,
      [id]: getEmailsError(newValue),
    }));
  };

  const handleAdd = () => {
    setPairs([...pairs, { id: Math.random(), key: '', value: [] }]);
  };

  const handleRemove = (id: number) => {
    const newPairs = pairs.filter((p) => p.id !== id);
    setPairs(newPairs);
    syncToForm(newPairs);
    setEmailErrors((prev) => omit(prev, id));
  };

  // Check if there are any missing emails for selected roles or invalid emails
  const hasMissingEmails = pairs.some(({ key, value }) => key && value.length === 0);
  const hasInvalidState = Object.values(emailErrors).some(Boolean) || hasMissingEmails;

  // Update the form state with the validation result,
  // so that the parent form can be aware of the validation status
  useEffect(() => {
    form.change(`${source}__hasErrors`, hasInvalidState ? true : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInvalidState]);

  return (
    <div>
      <Field
        name={`${source}__hasErrors`}
        validate={(value) => (value ? 'invalid' : undefined)}
        render={() => null}
      />
      {pairs.map(({ id, key, value }) => (
        <div key={id} className={localClasses.row}>
          <TextField
            select
            label={t('tenantNotificationsRole')}
            value={key}
            onChange={(e) => handleKeyChange(id, e.target.value)}
            className={localClasses.roleField}
            size='small'
            variant='filled'
          >
            {roles.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </TextField>
          <AutocompleteArrayTextInputComponent
            label={t('tenantNotificationsEmails')}
            value={value}
            onChange={(e, emails) => handleValueChange(id, emails)}
            freeSolo
            required={!!key}
            variant='filled'
            size='small'
            errorMessage={emailErrors[id]}
            regularHelperText={t('tenantNotificationsEmailsHelperText')}
            className={localClasses.emailField}
          />
          <IconButton
            size='small'
            onClick={() => handleRemove(id)}
            className={localClasses.deleteButton}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </div>
      ))}
      <Button startIcon={<AddCircleOutlineIcon />} color='default' onClick={handleAdd}>
        {t('tenantNotificationsAddRole')}
      </Button>
    </div>
  );
};

export const TenantNotificationsForm: FC<TenantNotificationsFormProps> = ({ keyName }) => {
  const translate = useTranslate();
  const t = (key: string): string => translate(l(`featuresConfig.${key}`));

  return (
    <>
      <H3 topSpacing={0} bottomSpacing={2}>
        {t('tenantNotificationsTitle')}
      </H3>
      <RoleGroupEmailsEditor source={`${keyName}.settings.roleGroupEmails`} />
    </>
  );
};
